// Persistent reminder while an account's address is unconfirmed. Site
// creation is blocked server-side until then, so this explains why — and
// offers the resend without a trip to the Account page.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MailWarning } from "../../components/icons";
import { api } from "../../api/api";
import { asApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { Spinner } from "../../components/ui";

export function UnverifiedBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user || user.email_verified) return null;

  async function resend() {
    setBusy(true);
    setError(null);
    try {
      await api.resendVerification();
      setSent(true);
    } catch (err) {
      setError(asApiError(err).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-4 rounded-lg border border-warn/40 bg-warn/10 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-start gap-2 text-sm">
        <MailWarning size={16} className="mt-0.5 shrink-0" />
        <span>
          {t("account.unverified", {
            defaultValue: "Confirm your email address ({{email}}) to finish activating your account.",
            email: user.email,
          })}
          {error && <span className="text-bad"> {error}</span>}
        </span>
      </div>
      {sent ? (
        <span className="text-sm text-good">
          {t("account.verificationSent", { defaultValue: "Confirmation email sent." })}
        </span>
      ) : (
        <button type="button" className="btn-ghost !py-1 text-xs" disabled={busy} onClick={resend}>
          {busy ? <Spinner size={12} /> : t("account.resendVerification", { defaultValue: "Resend email" })}
        </button>
      )}
    </div>
  );
}
